// Phrases that appear in subscription-blocked or usage-limit error messages.
// Components check this to skip their own toast when the global interceptor already fired one.
const SUBSCRIPTION_ERROR_PHRASES = [
  'subscription is not active',
  'subscription payment failed',
  'trial has ended',
  'monthly conversation limit is reached',
  'monthly template send limit is reached',
  'monthly bulk message send limit is reached',
  'limit is exceeded',
  'monthly limit is exceeded',
];

/**
 * Returns true when err is a subscription-enforcement or usage-limit error already handled
 * by the axios interceptor (networkErrorHandler.js). Components should skip
 * their own toast/snackbar when this returns true to avoid duplicate notifications.
 */
export function isSubscriptionBlockedError(err) {
  if (!err) return false;
  const text = typeof err === 'string' ? err : (err?.message || String(err));
  return SUBSCRIPTION_ERROR_PHRASES.some(p => text.toLowerCase().includes(p.toLowerCase()));
}

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

/**
 * Checks subscription status before performing a write action.
 * Returns { blocked: true, message } if the subscription blocks the action,
 * or { blocked: false } if the action should proceed.
 * Pass getAuthToken from utils/getAuthToken as the argument.
 */
export async function checkSubscriptionActive(getTokenFn) {
  try {
    const token = await getTokenFn();
    if (!token) return { blocked: false };
    const res = await fetch(`${SERVER_DOMAIN}/api/subscriptions/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    const sub = data?.subscription;
    if (!sub) return { blocked: false };
    const now = new Date();
    const trialEnded = sub.status === 'trial' && sub.trialEnd && new Date(sub.trialEnd) < now;
    if (sub.status === 'expired' || sub.status === 'cancelled') {
      return { blocked: true, message: 'Your subscription is not active. Please contact ClusterDen to restore access.' };
    }
    if (sub.status === 'payment_failed') {
      return { blocked: true, message: 'Your subscription payment failed. Outbound actions are disabled until billing is resolved.' };
    }
    if (trialEnded) {
      return { blocked: true, message: 'Your trial has ended. Please upgrade your plan to continue using ClusterDen.' };
    }
    return { blocked: false };
  } catch {
    return { blocked: false }; // If check fails, let server enforce
  }
}

// ─── Cached pre-send outbound guard ────────────────────────────────────────

// Cache stores the raw API response so different feature-key sets can be
// evaluated against the same fetch without an extra network call.
let _outboundCache = null; // { data: { subscription, features, ... }, fetchedAt }
const OUTBOUND_CACHE_TTL_MS = 90 * 1000; // 90 seconds

// Human-readable messages matching what the backend returns for each feature.
const FEATURE_LIMIT_MESSAGES = {
  conversations: 'Your monthly conversation limit is reached. Please upgrade your plan before sending more messages.',
  templateSends: 'Your monthly template send limit is reached. Please upgrade your plan.',
  bulkMessageSends: 'Your monthly bulk message send limit is reached. Please upgrade your plan.',
  teamsCreated: 'Your team limit is reached. Please upgrade your plan to create more teams.',
  contacts: 'Your monthly contact limit is reached. Please upgrade your plan before adding more contacts.',
  contactGroupsCreated: 'Your contact group limit is reached. Please upgrade your plan to create more groups.',
  teamMembersCreated: 'Your team member limit is reached. Please upgrade your plan to add more members.',
  tasksCreated: 'Your task limit is reached. Please upgrade your plan to create more tasks.',
  taskTemplatesCreated: 'Your task template limit is reached. Please upgrade your plan to create more task templates.',
  workspacesCreated: 'Your workspace limit is reached. Please upgrade your plan to add more workspaces.',
  bulkCampaignsCreated: 'Your bulk campaign limit is reached. Please upgrade your plan.',
};

function evaluateOutboundData(data, checkFeatures) {
  const sub = data?.subscription;
  if (sub) {
    const trialEnded = sub.status === 'trial' && sub.trialEnd && new Date(sub.trialEnd) < new Date();
    if (sub.status === 'expired' || sub.status === 'cancelled') {
      return { blocked: true, message: 'Your subscription is not active. Please contact ClusterDen to restore access.' };
    }
    if (sub.status === 'payment_failed') {
      return { blocked: true, message: 'Your subscription payment failed. Outbound actions are disabled until billing is resolved.' };
    }
    if (trialEnded) {
      return { blocked: true, message: 'Your trial has ended. Please upgrade your plan to continue using ClusterDen.' };
    }
  }

  // Check per-feature usage limits
  const features = data?.features || [];
  for (const key of checkFeatures) {
    const feature = features.find((f) => f.key === key);
    if (feature && feature.limit > 0 && feature.used >= feature.limit) {
      const msg = FEATURE_LIMIT_MESSAGES[key] || 'Your monthly limit is exceeded. Please upgrade your plan.';
      return { blocked: true, message: msg };
    }
  }

  return { blocked: false };
}

/**
 * Checks subscription status AND usage limits before any outbound send.
 * API response is cached for 90 s; the raw data is reused across calls with
 * different checkFeatures sets without additional fetches.
 *
 * @param {Function} getTokenFn  - getAuthToken from utils/getAuthToken
 * @param {string[]} checkFeatures - feature keys to check (default: ['conversations'])
 * @returns {{ blocked: boolean, message?: string }}
 */
export async function checkBeforeOutbound(getTokenFn, checkFeatures = ['conversations']) {
  const now = Date.now();
  let data;

  if (_outboundCache && now - _outboundCache.fetchedAt < OUTBOUND_CACHE_TTL_MS) {
    data = _outboundCache.data;
  } else {
    try {
      const token = await getTokenFn();
      if (!token) return { blocked: false };
      const res = await fetch(`${SERVER_DOMAIN}/api/subscriptions/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return { blocked: false };
      data = await res.json();
      _outboundCache = { data, fetchedAt: Date.now() };
    } catch {
      return { blocked: false }; // fail open — server enforces anyway
    }
  }

  return evaluateOutboundData(data, checkFeatures);
}

/** Call this when subscription state is known to have changed (e.g. plan upgrade). */
export function invalidateOutboundCache() {
  _outboundCache = null;
}
